import {
  useLayoutEffect,
  useRef,
  type ReactElement,
  type ReactNode,
} from "react";
import { useTransitionPresence } from "../hooks/useTransitionPresence.js";
import { cn } from "../lib/utils.js";
import { MOTION_DURATION_MS } from "../motion.js";

export interface WorkspaceControlSlotProps {
  readonly visible: boolean;
  readonly children: ReactNode;
  readonly className?: string;
  /** Keep a collapsed trigger in the DOM when a portalled overlay needs it. */
  readonly retainWhenHidden?: boolean;
}

/**
 * Retains a folding workspace action for the shared state transition, then
 * removes it from layout, accessibility and interaction together.
 */
export function WorkspaceControlSlot({
  visible,
  children,
  className,
  retainWhenHidden = false,
}: WorkspaceControlSlotProps): ReactElement | null {
  const { present, phase } = useTransitionPresence(
    visible,
    MOTION_DURATION_MS.state,
  );
  const nodeRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const node = nodeRef.current;
    if (!node) return;
    let focusTarget: HTMLElement | null = null;
    if (!visible && node.contains(document.activeElement)) {
      focusTarget = node.closest<HTMLElement>("[role='group']");
      focusTarget?.focus();
    }
    node.toggleAttribute("inert", !visible);
    // Chromium may blur once more when `inert` is applied even after focus has
    // moved outside the subtree. Restore the same group within this commit.
    if (focusTarget && document.activeElement !== focusTarget) {
      focusTarget.focus();
    }
  }, [present, visible]);
  if (!present && !retainWhenHidden) return null;
  const atRest = phase === "open";

  return (
    <div
      ref={nodeRef}
      data-motion-state={phase}
      aria-hidden={!visible ? "true" : undefined}
      className={cn(
        "grid shrink-0 overflow-hidden transition-[grid-template-columns,opacity,transform] duration-motion-state ease-motion-standard",
        atRest
          ? "grid-cols-[1fr] translate-x-0 opacity-100"
          : "pointer-events-none grid-cols-[0fr] translate-x-1 opacity-0 motion-reduce:translate-x-0",
        className,
      )}
    >
      <div className="min-w-0 overflow-hidden">{children}</div>
    </div>
  );
}
